import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, CheckCircle, XCircle, AlertCircle, SlidersHorizontal, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// Re-usable header cell with vertical-text label (reads bottom→top; no clipping).
function RotatedHeader({ label }) {
  return (
    <th
      className="align-bottom text-left px-0 w-[44px] min-w-[44px] border-b border-slate-200 bg-white"
      title={label}
    >
      <div className="flex items-end justify-start pb-1 pl-1">
        <span
          className="text-xs font-semibold text-slate-700 whitespace-nowrap py-2"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          {label}
        </span>
      </div>
    </th>
  );
}

export default function ComplianceDashboard() {
  const [personnel, setPersonnel] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [inductionTypes, setInductionTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('certifications');

  // New filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all, issues, compliant
  const [hiddenCerts, setHiddenCerts] = useState([]);
  const [hiddenInds, setHiddenInds] = useState([]);
  const [showColumnPanel, setShowColumnPanel] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [persRes, certRes, indRes] = await Promise.all([
        axios.get(`${API}/personnel`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=certification_type`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=induction_type`, { headers: getAuthHeader() })
      ]);
      setPersonnel(persRes.data);
      setCertTypes(certRes.data.map(c => c.name));
      setInductionTypes(indRes.data.map(c => c.name));
    } catch (error) { /* fail silently */ }
  };

  const uniqueRoles = useMemo(
    () => [...new Set(personnel.map(p => p.role).filter(Boolean))].sort(),
    [personnel]
  );
  const uniqueDepartments = useMemo(
    () => [...new Set(personnel.map(p => p.department).filter(Boolean))].sort(),
    [personnel]
  );

  // Status helpers
  const getCertStatus = (person, certName) => {
    const cert = (person.certifications || []).find(c => c.type === certName || c.name === certName);
    if (!cert) return 'missing';
    if (cert.expiry_date) {
      const expiry = new Date(cert.expiry_date);
      const now = new Date();
      const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
      if (daysUntilExpiry < 0) return 'expired';
      if (daysUntilExpiry < 30) return 'expiring';
    }
    return 'valid';
  };

  const getInductionStatus = (person, indName) => {
    const ind = (person.inductions || []).find(i => i.induction_type === indName || i.name === indName || i.site === indName);
    if (!ind) return 'missing';
    if (ind.expiry_date) {
      const expiry = new Date(ind.expiry_date);
      const now = new Date();
      const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
      if (daysUntilExpiry < 0) return 'expired';
      if (daysUntilExpiry < 30) return 'expiring';
    }
    return 'valid';
  };

  // Person has at least one issue (missing/expired/expiring) in the currently-visible cert/ind scope
  const personHasIssue = (person, scope) => {
    const types = scope === 'inductions' ? inductionTypes.filter(t => !hiddenInds.includes(t))
      : certTypes.filter(t => !hiddenCerts.includes(t));
    const statusFn = scope === 'inductions' ? getInductionStatus : getCertStatus;
    return types.some(t => ['missing', 'expired', 'expiring'].includes(statusFn(person, t)));
  };

  // Filter personnel list
  const filteredPersonnel = useMemo(() => {
    return personnel.filter(p => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(p.name || '').toLowerCase().includes(q)) return false;
      }
      if (roleFilter !== 'all' && p.role !== roleFilter) return false;
      if (departmentFilter !== 'all' && p.department !== departmentFilter) return false;
      if (statusFilter !== 'all') {
        const issue = personHasIssue(p, activeTab === 'inductions' ? 'inductions' : 'certifications');
        if (statusFilter === 'issues' && !issue) return false;
        if (statusFilter === 'compliant' && issue) return false;
      }
      return true;
    });
  }, [personnel, searchQuery, roleFilter, departmentFilter, statusFilter, activeTab, certTypes, inductionTypes, hiddenCerts, hiddenInds]);

  // Visible columns
  const visibleCertTypes = useMemo(() => certTypes.filter(t => !hiddenCerts.includes(t)), [certTypes, hiddenCerts]);
  const visibleInductionTypes = useMemo(() => inductionTypes.filter(t => !hiddenInds.includes(t)), [inductionTypes, hiddenInds]);

  const StatusIcon = ({ status }) => {
    if (status === 'valid') return <CheckCircle className="w-4 h-4 text-emerald-600" />;
    if (status === 'expired') return <XCircle className="w-4 h-4 text-red-500" />;
    if (status === 'expiring') return <AlertCircle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-slate-300" />;
  };

  // Summary counts (based on visible scope + filtered personnel)
  const certSummary = useMemo(() => {
    return visibleCertTypes.map(ct => {
      const valid = filteredPersonnel.filter(p => getCertStatus(p, ct) === 'valid').length;
      const expiring = filteredPersonnel.filter(p => getCertStatus(p, ct) === 'expiring').length;
      const expired = filteredPersonnel.filter(p => getCertStatus(p, ct) === 'expired').length;
      const missing = filteredPersonnel.filter(p => getCertStatus(p, ct) === 'missing').length;
      return { name: ct, valid, expiring, expired, missing };
    });
  }, [visibleCertTypes, filteredPersonnel]);

  const indSummary = useMemo(() => {
    return visibleInductionTypes.map(it => {
      const valid = filteredPersonnel.filter(p => getInductionStatus(p, it) === 'valid').length;
      const missing = filteredPersonnel.filter(p => getInductionStatus(p, it) === 'missing').length;
      return { name: it, valid, missing };
    });
  }, [visibleInductionTypes, filteredPersonnel]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setDepartmentFilter('all');
    setStatusFilter('all');
    setHiddenCerts([]);
    setHiddenInds([]);
  };

  const hasAnyFilter = searchQuery || roleFilter !== 'all' || departmentFilter !== 'all' || statusFilter !== 'all' || hiddenCerts.length > 0 || hiddenInds.length > 0;

  const toggleCertColumn = (ct) => {
    setHiddenCerts(prev => prev.includes(ct) ? prev.filter(c => c !== ct) : [...prev, ct]);
  };
  const toggleIndColumn = (it) => {
    setHiddenInds(prev => prev.includes(it) ? prev.filter(i => i !== it) : [...prev, it]);
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="compliance-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">Compliance Matrix</h1>
        <p className="text-sm text-slate-600 mt-2">At-a-glance certification, induction, and medical compliance status for all personnel</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-sm font-semibold text-slate-500 mb-1">Showing Personnel</div>
            <div className="text-3xl font-bold text-slate-900">{filteredPersonnel.length}<span className="text-base text-slate-400 font-normal"> / {personnel.length}</span></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-sm font-semibold text-slate-500 mb-1">Certification Types</div>
            <div className="text-3xl font-bold text-slate-900">{visibleCertTypes.length}<span className="text-base text-slate-400 font-normal"> / {certTypes.length}</span></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-sm font-semibold text-slate-500 mb-1">Induction Types</div>
            <div className="text-3xl font-bold text-slate-900">{visibleInductionTypes.length}<span className="text-base text-slate-400 font-normal"> / {inductionTypes.length}</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 shadow-sm" data-testid="compliance-filter-bar">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="compliance-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name..." className="pl-10 h-9" />
          </div>

          <div>
            <Label className="sr-only">Role</Label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 text-xs min-w-[160px]"
              data-testid="compliance-role-filter"
            >
              <option value="all">All Roles</option>
              {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <Label className="sr-only">Department</Label>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 text-xs min-w-[160px]"
              data-testid="compliance-department-filter"
            >
              <option value="all">All Departments</option>
              {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div>
            <Label className="sr-only">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 text-xs min-w-[160px]"
              data-testid="compliance-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="issues">Has Issues (missing/expired/expiring)</option>
              <option value="compliant">Fully Compliant</option>
            </select>
          </div>

          {activeTab !== 'medical' && (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-9 text-xs"
                onClick={() => setShowColumnPanel(s => !s)}
                data-testid="compliance-columns-btn"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
                Columns {activeTab === 'inductions' ? `(${visibleInductionTypes.length}/${inductionTypes.length})` : `(${visibleCertTypes.length}/${certTypes.length})`}
              </Button>
              {showColumnPanel && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-md shadow-lg p-3 z-30 w-72 max-h-80 overflow-y-auto" data-testid="compliance-columns-panel">
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                    <span className="text-xs font-semibold text-slate-700">
                      {activeTab === 'inductions' ? 'Induction Types' : 'Certification Types'}
                    </span>
                    <button
                      type="button"
                      onClick={() => activeTab === 'inductions' ? setHiddenInds([]) : setHiddenCerts([])}
                      className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                    >
                      Show All
                    </button>
                  </div>
                  {(activeTab === 'inductions' ? inductionTypes : certTypes).map(t => {
                    const isHidden = (activeTab === 'inductions' ? hiddenInds : hiddenCerts).includes(t);
                    return (
                      <label key={t} className="flex items-center gap-2 py-1 hover:bg-slate-50 rounded px-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => activeTab === 'inductions' ? toggleIndColumn(t) : toggleCertColumn(t)}
                          className="rounded border-slate-300"
                        />
                        <span className="text-xs text-slate-700">{t}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {hasAnyFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-9 text-xs text-slate-500 hover:text-slate-700 ml-auto"
              data-testid="compliance-clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 text-xs mb-4">
              <TabsTrigger value="certifications" data-testid="compliance-certs-tab">Certifications</TabsTrigger>
              <TabsTrigger value="inductions" data-testid="compliance-inductions-tab">Inductions</TabsTrigger>
              <TabsTrigger value="medical" data-testid="compliance-medical-tab">Medical</TabsTrigger>
            </TabsList>

            {/* Certifications Matrix */}
            <TabsContent value="certifications">
              <div className="flex flex-wrap gap-3 mb-4">
                {certSummary.map(cs => (
                  <div key={cs.name} className="flex items-center gap-2 text-xs border border-slate-200 rounded-md px-3 py-1.5">
                    <span className="font-medium text-slate-700 truncate max-w-[150px]">{cs.name}</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{cs.valid}</Badge>
                    {cs.expiring > 0 && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{cs.expiring}</Badge>}
                    {cs.expired > 0 && <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">{cs.expired}</Badge>}
                    <Badge className="bg-slate-50 text-slate-500 border-slate-200 text-xs">{cs.missing}</Badge>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto overflow-y-visible">
                <table className="text-sm border-separate border-spacing-0" data-testid="cert-matrix" style={{ tableLayout: 'fixed', width: `${200 + visibleCertTypes.length * 44}px` }}>
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-semibold text-slate-700 py-2 px-3 sticky left-0 bg-white w-[200px] min-w-[200px] max-w-[200px] align-bottom border-b border-slate-200 z-10">
                        <div className="pb-1">Personnel</div>
                      </th>
                      {visibleCertTypes.map(ct => (
                        <RotatedHeader key={ct} label={ct} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800 sticky left-0 bg-white border-b border-slate-100 w-[200px] min-w-[200px] max-w-[200px]">
                          <div className="truncate" title={p.name}>{p.name}</div>
                          {p.role && <div className="text-[10px] text-slate-500 font-normal truncate" title={`${p.role}${p.department ? ' • ' + p.department : ''}`}>{p.role}{p.department ? ` • ${p.department}` : ''}</div>}
                        </td>
                        {visibleCertTypes.map(ct => (
                          <td key={ct} className="text-left py-2 pl-2 border-b border-slate-100 w-[44px] min-w-[44px]">
                            <StatusIcon status={getCertStatus(p, ct)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredPersonnel.length === 0 && <p className="text-center text-slate-500 py-8 text-sm">No personnel match the current filters</p>}
              {visibleCertTypes.length === 0 && <p className="text-center text-slate-500 py-4 text-xs">All columns hidden — use the Columns button to show some.</p>}
            </TabsContent>

            {/* Inductions Matrix */}
            <TabsContent value="inductions">
              <div className="flex flex-wrap gap-3 mb-4">
                {indSummary.map(is => (
                  <div key={is.name} className="flex items-center gap-2 text-xs border border-slate-200 rounded-md px-3 py-1.5">
                    <span className="font-medium text-slate-700 truncate max-w-[180px]">{is.name}</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{is.valid}</Badge>
                    <Badge className="bg-slate-50 text-slate-500 border-slate-200 text-xs">{is.missing}</Badge>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto overflow-y-visible">
                <table className="text-sm border-separate border-spacing-0" data-testid="induction-matrix" style={{ tableLayout: 'fixed', width: `${200 + visibleInductionTypes.length * 44}px` }}>
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-semibold text-slate-700 py-2 px-3 sticky left-0 bg-white w-[200px] min-w-[200px] max-w-[200px] align-bottom border-b border-slate-200 z-10">
                        <div className="pb-1">Personnel</div>
                      </th>
                      {visibleInductionTypes.map(it => (
                        <RotatedHeader key={it} label={it} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800 sticky left-0 bg-white border-b border-slate-100 w-[200px] min-w-[200px] max-w-[200px]">
                          <div className="truncate" title={p.name}>{p.name}</div>
                          {p.role && <div className="text-[10px] text-slate-500 font-normal truncate" title={`${p.role}${p.department ? ' • ' + p.department : ''}`}>{p.role}{p.department ? ` • ${p.department}` : ''}</div>}
                        </td>
                        {visibleInductionTypes.map(it => (
                          <td key={it} className="text-left py-2 pl-2 border-b border-slate-100 w-[44px] min-w-[44px]">
                            <StatusIcon status={getInductionStatus(p, it)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredPersonnel.length === 0 && <p className="text-center text-slate-500 py-8 text-sm">No personnel match the current filters</p>}
              {visibleInductionTypes.length === 0 && <p className="text-center text-slate-500 py-4 text-xs">All columns hidden — use the Columns button to show some.</p>}
            </TabsContent>

            {/* Medical Summary */}
            <TabsContent value="medical">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="medical-matrix">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-xs font-semibold text-slate-700 py-2 px-3 min-w-[200px]">Personnel</th>
                      <th className="text-center text-xs font-semibold text-slate-700 py-2 px-3">Last PEM Date</th>
                      <th className="text-center text-xs font-semibold text-slate-700 py-2 px-3">PEM Result</th>
                      <th className="text-center text-xs font-semibold text-slate-700 py-2 px-3">Last LabDAS Date</th>
                      <th className="text-center text-xs font-semibold text-slate-700 py-2 px-3">LabDAS Result</th>
                      <th className="text-center text-xs font-semibold text-slate-700 py-2 px-3">Project PEM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map(p => {
                      const pemDate = p.pem_date ? new Date(p.pem_date).toLocaleDateString('en-AU') : '-';
                      const labdasDate = p.labdas_date ? new Date(p.labdas_date).toLocaleDateString('en-AU') : '-';
                      const pemResult = p.pem_result;
                      const labdasResult = p.labdas_result;
                      const pemBadge = pemResult === 'green' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : pemResult === 'orange' ? 'bg-amber-50 text-amber-700 border-amber-200' : pemResult === 'red' ? 'bg-red-50 text-red-700 border-red-200' : '';
                      const labdasBadge = labdasResult === 'negative' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : labdasResult === 'positive' ? 'bg-red-50 text-red-700 border-red-200' : '';
                      return (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-800">
                            <div>{p.name}</div>
                            {p.role && <div className="text-[10px] text-slate-500 font-normal">{p.role}{p.department ? ` • ${p.department}` : ''}</div>}
                          </td>
                          <td className="text-center py-2 px-3 text-slate-600">{pemDate}</td>
                          <td className="text-center py-2 px-3">{pemResult ? <Badge className={`capitalize ${pemBadge}`}>{pemResult}</Badge> : <span className="text-slate-300">-</span>}</td>
                          <td className="text-center py-2 px-3 text-slate-600">{labdasDate}</td>
                          <td className="text-center py-2 px-3">{labdasResult ? <Badge className={`capitalize ${labdasBadge}`}>{labdasResult}</Badge> : <span className="text-slate-300">-</span>}</td>
                          <td className="text-center py-2 px-3">
                            {(() => {
                              const list = Array.isArray(p.project_pems) ? p.project_pems : [];
                              if (list.length === 0) return <span className="text-slate-300">-</span>;
                              const colorFor = (r) => {
                                const v = (r || '').toString().toLowerCase();
                                if (v === 'green') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
                                if (v === 'orange' || v === 'amber') return 'bg-amber-50 text-amber-700 border-amber-200';
                                if (v === 'red') return 'bg-red-50 text-red-700 border-red-200';
                                return 'bg-blue-50 text-blue-700 border-blue-200';
                              };
                              return (
                                <div className="flex flex-col items-center gap-1" data-testid={`project-pems-${p.id}`}>
                                  {list.map((pem, idx) => {
                                    const dateStr = pem.pem_date ? new Date(pem.pem_date).toLocaleDateString('en-AU') : '';
                                    const label = `${pem.project || 'Project'}${dateStr ? ` • ${dateStr}` : ''}`;
                                    return (
                                      <Badge
                                        key={pem.id || idx}
                                        title={pem.notes ? `${label} — ${pem.notes}` : label}
                                        className={`text-[10px] ${colorFor(pem.result)}`}
                                      >
                                        {label}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredPersonnel.length === 0 && <p className="text-center text-slate-500 py-8 text-sm">No personnel match the current filters</p>}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-6 text-xs text-slate-500 flex-wrap">
        <div className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Valid / Active</div>
        <div className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Expiring Soon (&lt;30 days)</div>
        <div className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-red-500" /> Expired</div>
        <div className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-slate-300" /> Missing</div>
      </div>
    </div>
  );
}
